import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { clientsAPI, devisAPI } from '@/lib/api';
import { formatDate, formatDateTime, formatPrice, getStatusColor, getStatusLabel, getActivityIcon, cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { ParcoursComplet } from '@/components/ParcoursCommercial';
import ClientOpportunityTracker from '@/components/ClientOpportunityTracker';
import {
  ArrowLeft,
  Building2,
  Phone,
  Mail,
  MapPin,
  FileText,
  Plus,
  Globe,
  Tractor,
  HardHat,
  Construction,
  MoreHorizontal,
  Clock,
  Send,
  RefreshCw,
  CheckCircle,
  XCircle,
  StickyNote,
  MessageSquare,
  Target,
} from 'lucide-react';

const CATEGORY_CONFIG = {
  agriculteur: { label: 'Agriculteur', icon: Tractor, color: 'bg-green-100 text-green-700' },
  btp: { label: 'BTP', icon: HardHat, color: 'bg-orange-100 text-orange-700' },
  vrd: { label: 'VRD', icon: Construction, color: 'bg-blue-100 text-blue-700' },
  autres: { label: 'Autres', icon: MoreHorizontal, color: 'bg-gray-100 text-gray-700' },
};

const ClientDetailPage = () => {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [clientRes, devisRes] = await Promise.all([
        clientsAPI.getOne(id),
        devisAPI.getAll(),
      ]);
      setClient(clientRes.data);
      setDevis(devisRes.data.filter((d) => d.client_id === id));
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const getActivityIconComponent = (type) => {
    const icons = {
      appel: Phone,
      sms: MessageSquare,
      mail: Mail,
      note: StickyNote,
      devis_envoye: Send,
      devis_relance: RefreshCw,
      devis_cloture: XCircle,
      devis_accepte: CheckCircle,
      devis_refuse: XCircle,
    };
    return icons[type] || Clock;
  };

  // Build full address for Google Maps
  const getFullAddress = () => {
    const parts = [];
    if (client?.address) parts.push(client.address);
    if (client?.postal_code) parts.push(client.postal_code);
    if (client?.city) parts.push(client.city);
    if (client?.country) parts.push(client.country);
    return parts.join(', ');
  };

  const getGoogleMapsUrl = () => {
    const address = getFullAddress();
    if (!address) return null;
    return `https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(address)}&zoom=14&size=400x200&markers=color:red%7C${encodeURIComponent(address)}&key=`;
  };

  const getGoogleMapsLinkUrl = () => {
    const address = getFullAddress();
    if (!address) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Client non trouvé</p>
        <Link to="/admin/clients">
          <Button variant="link">Retour aux clients</Button>
        </Link>
      </div>
    );
  }

  const categoryConfig = client.category ? CATEGORY_CONFIG[client.category] : null;
  const CategoryIcon = categoryConfig?.icon || MoreHorizontal;

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin/clients">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
              {client.name}
            </h1>
            {categoryConfig && (
              <Badge className={categoryConfig.color}>
                <CategoryIcon className="w-3 h-3 mr-1" />
                {categoryConfig.label}
              </Badge>
            )}
            {client.client_type && (
              <Badge className="bg-slate-100 text-slate-600 capitalize">
                {client.client_type}
              </Badge>
            )}
          </div>
          {client.company && (
            <p className="text-slate-500 flex items-center gap-1 mt-1">
              <Building2 className="w-4 h-4" />
              {client.company}
            </p>
          )}
        </div>
        <Link to={`/admin/devis/nouveau?client=${client.id}`}>
          <Button className="bg-yellow-500 hover:bg-yellow-600 text-slate-900" data-testid="new-devis-for-client-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau devis
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Client Info */}
        <div className="space-y-6">
          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50">
              <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Informations</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {client.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <a href={`mailto:${client.email}`} className="text-slate-600 hover:text-slate-800">
                    {client.email}
                  </a>
                </div>
              )}
              {client.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-slate-400" />
                  <a href={`tel:${client.phone}`} className="text-slate-600 hover:text-slate-800">
                    {client.phone}
                  </a>
                </div>
              )}
              {(client.address || client.city || client.postal_code) && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div className="text-slate-600">
                    {client.address && <p>{client.address}</p>}
                    {(client.postal_code || client.city) && (
                      <p>{[client.postal_code, client.city].filter(Boolean).join(' ')}</p>
                    )}
                    {client.country && (
                      <p className="flex items-center gap-1 text-sm text-slate-500">
                        <Globe className="w-3 h-3" />
                        {client.country}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {client.notes && (
                <div className="pt-4 border-t border-slate-100">
                  <p className="text-sm text-slate-500 mb-1">Notes</p>
                  <p className="text-slate-600">{client.notes}</p>
                </div>
              )}
              <div className="pt-4 border-t border-slate-100">
                <p className="text-sm text-slate-500">
                  Client depuis le {formatDate(client.created_at)}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Google Maps Static - Only show if we have address */}
          {getFullAddress() && (
            <Card className="border-slate-200 overflow-hidden">
              <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3">
                <CardTitle className="text-sm" style={{ fontFamily: 'Barlow Condensed' }}>
                  Localisation
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <a 
                  href={getGoogleMapsLinkUrl()} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                >
                  <div className="bg-slate-100 h-48 flex items-center justify-center hover:bg-slate-200 transition-colors">
                    <div className="text-center">
                      <MapPin className="w-8 h-8 text-red-500 mx-auto mb-2" />
                      <p className="text-sm text-slate-600 px-4">{getFullAddress()}</p>
                      <p className="text-xs text-blue-600 mt-2">Voir sur Google Maps →</p>
                    </div>
                  </div>
                </a>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Devis History & Activities */}
        <div className="lg:col-span-2 space-y-6">
          {/* Opportunités / Suivi Commercial */}
          <ClientOpportunityTracker clientId={id} clientName={client.name} />

          {/* Devis History */}
          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>
                Devis ({devis.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {devis.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  Aucun devis pour ce client
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {devis.map((d) => (
                    <Link
                      key={d.id}
                      to={`/admin/devis/${d.id}`}
                      className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                      data-testid={`client-devis-${d.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-mono text-sm font-medium text-slate-800">
                            {d.reference}
                          </p>
                          <p className="text-sm text-slate-500">
                            {d.mini_pelle_name} • {formatDate(d.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge className={getStatusColor(d.status)}>
                          {getStatusLabel(d.status)}
                        </Badge>
                        <span className="font-mono font-medium text-slate-800">
                          {formatPrice(d.total)}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Activity History */}
          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50">
              <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>
                Historique des activités
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {(!client.activities || client.activities.length === 0) ? (
                <div className="p-8 text-center text-slate-500">
                  <Clock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p>Aucune activité enregistrée</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Les envois de devis et relances apparaîtront ici
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                  {[...client.activities].reverse().map((activity, idx) => {
                    const IconComponent = getActivityIconComponent(activity.type);
                    const iconConfig = getActivityIcon(activity.type);
                    
                    return (
                      <div key={activity.id || idx} className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", iconConfig.color)}>
                            <IconComponent className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-800">{activity.content}</p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                              <span>{formatDateTime(activity.created_at)}</span>
                              <span>•</span>
                              <span>{activity.created_by_name}</span>
                              {activity.devis_reference && (
                                <>
                                  <span>•</span>
                                  <Link 
                                    to={`/admin/devis/${activity.devis_id}`}
                                    className="text-blue-600 hover:underline"
                                  >
                                    {activity.devis_reference}
                                  </Link>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ClientDetailPage;
